import React, { useEffect, useState } from 'react';
import './questionDisplay.scss';
import { useIsMobile } from '../../hooks/isMobile';
import DOMPurify from 'dompurify';
import { useDispatch, useSelector } from 'react-redux';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../hooks/Dialog';
import ExamStatusBar from '../ExamStatusBar/ExamStatusBar';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { getQuestions } from '../../../../store/ExamSlice';

const QuestionDisplay = ({ lessonId, activeSection, onNextSection, mockTestId, userId }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const storeVar = useSelector(state => state.examDetails);
  const questionData = storeVar?.questionData || [];

  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState({});
  const [answeredQuestions, setAnsweredQuestions] = useState([]);
  const [markedQuestions, setMarkedQuestions] = useState([]);
  const [visitedQuestions, setVisitedQuestions] = useState([]);
  const [tempSelectedAnswer, setTempSelectedAnswer] = useState(null);
  const [sectionState, setSectionState] = useState({});
  const [questionPaperDialogOpen, setQuestionPaperDialogOpen] = useState(false);
  const [instructionsDialogOpen, setInstructionsDialogOpen] = useState(false);
  const [showSidebar, setShowSidebar] = useState(false);
  const isMobile = useIsMobile();
  const [examLanguage, setExamLanguage] = useState('english');

  useEffect(() => {
    if (lessonId) {
      dispatch(getQuestions(userId, lessonId));
      setCurrentQuestionIndex(0);

      const section = sectionState[lessonId] || {
        selectedAnswers: {},
        answeredQuestions: [],
        markedQuestions: [],
        visitedQuestions: [],
      };

      setSelectedAnswers(section.selectedAnswers);
      setAnsweredQuestions(section.answeredQuestions);
      setMarkedQuestions(section.markedQuestions);
      setVisitedQuestions(section.visitedQuestions);
    }
  }, [lessonId]);

  useEffect(() => {
    setSectionState(prev => ({
      ...prev,
      [lessonId]: {
        selectedAnswers,
        answeredQuestions,
        markedQuestions,
        visitedQuestions,
      },
    }));
  }, [selectedAnswers, answeredQuestions, markedQuestions, visitedQuestions]);

  useEffect(() => {
    const saved = selectedAnswers[currentQuestionIndex];
    setTempSelectedAnswer(saved ?? null);
  }, [currentQuestionIndex, selectedAnswers]);

  if (!questionData.length) return <div>Loading questions...</div>;
  const currentQuestion = questionData[currentQuestionIndex];
  const hasEnglish = currentQuestion?.questionEnglish?.trim() !== '';
  const hasBengali = currentQuestion?.questionBengali?.trim() !== '';

  const getAnswerHtml = (num) =>
    DOMPurify.sanitize(
      examLanguage === 'english'
        ? currentQuestion[`answer${num}English`]
        : currentQuestion[`answer${num}Bengali`]
    );

  const getQuestionHtml = () =>
    DOMPurify.sanitize(
      examLanguage === 'english'
        ? currentQuestion?.questionEnglish
        : currentQuestion?.questionBengali
    );



  const toggleSidebar = () => setShowSidebar(!showSidebar);

  const onQuestionChange = (index) => {
    setCurrentQuestionIndex(index);
    if (!visitedQuestions.includes(index)) {
      setVisitedQuestions([...visitedQuestions, index]);
    }
  };



  const getQuestionClass = (num) => {
    let className = "question-button";
    const question = questionData[num];
    const isCorrect = question?.selectedAnswer === question?.correctAnswer;

    if (currentQuestionIndex === num) className += " current";

    if (question?.selectedAnswer == null) {
      className += " not-visited";
    } else if (isCorrect) {
      className += " answered";
    } else {
      className += " visited";
    }

    return className;
  };


  const handleSubmitTest = () => {
    // dispatch(submitTest(navigate));
  };

  return (
    <>

      <ExamStatusBar
        username="O2"
        answeredCount={questionData.filter(q => q.selectedAnswer === q.correctAnswer).length}
        notAnsweredCount={questionData.filter(q => q.selectedAnswer != null && q.selectedAnswer !== q.correctAnswer).length}
        markedCount={questionData.filter((_, index) => markedQuestions.includes(index)).length}
        notVisitedCount={questionData.filter(q => q.selectedAnswer == null).length}
        onReport={() => { }}
        examLanguage={examLanguage}
        setExamLanguage={setExamLanguage}
        hasEnglish={hasEnglish}
        hasBengali={hasBengali}
      />

      {isMobile && (
        <div className="mobile-header">
          <div>Question {currentQuestionIndex + 1}</div>
          <button onClick={toggleSidebar}>
            {showSidebar ? "Hide Panel" : "Show Panel"}
          </button>
        </div>
      )}

      <div className="main-content">
        <div className={`question-area ${isMobile && showSidebar ? 'hidden' : ''}`}>
          <div className="question-card">
            <div className="question-header">
              <span className="question-number">Question No. {currentQuestionIndex + 1}</span>
              <div className="marks-info">
                <span>Marks</span>
                <span className="mark-positive">+{storeVar.examData.correctMarks ?? 1}</span>
                <span className="mark-negative">-{storeVar.examData.inCorrectMarks ?? 0}</span>
              </div>
            </div>

            <div className="question-body">
              <p
                className="question-text"
                dangerouslySetInnerHTML={{ __html: getQuestionHtml() }}
              ></p>

              <div className="radio-group preview-mode">
                {[1, 2, 3, 4].map((num) => {
                  const isCorrect = currentQuestion?.correctAnswer === num;
                  const isSelected = currentQuestion?.selectedAnswer === num;

                  let className = "radio-option";
                  if (isSelected && isCorrect) className += " correct";
                  else if (isSelected && !isCorrect) className += " wrong";
                  else if (isCorrect) className += " correct";

                  return (
                    <div key={num} className={className}>
                      <div className="answer-indicator">
                        {isCorrect ? "✔️" : isSelected ? "❌" : ""}
                      </div>
                      <label dangerouslySetInnerHTML={{ __html: getAnswerHtml(num) }}></label>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
          <div className="question-controls-wrapper">
            <div className={`question-controls-container ${isMobile ? 'mobile' : ''}`} style={{ justifyContent: 'flex-end' }}>
              <button
                className="primary-button"
                onClick={() => {
                  if (currentQuestionIndex < questionData.length - 1) {
                    setCurrentQuestionIndex(currentQuestionIndex + 1);
                  } else if (typeof onNextSection === 'function') {
                    onNextSection();
                  }
                }}
              >
                {currentQuestionIndex === questionData.length - 1 ? 'Next Section' : 'Next'}
              </button>
            </div>
          </div>

        </div>

        <div className={`sidebar ${isMobile && !showSidebar ? 'hidden' : ''}`}>
          <div className="question-pagination">
            <div className="topBox">
              <div className="section-title">SECTION : {activeSection}</div>
              <div className="question-grid">
                {questionData.map((_, i) => (
                  <button
                    key={i}
                    className={getQuestionClass(i)}
                    onClick={() => onQuestionChange(i)}
                  >
                    {i + 1}
                  </button>
                ))}
              </div>

              <div className="legend-box">
                <div className="legend-item answered"><div className="dot" /><span>Correct Answer</span></div>
                <div className="legend-item not-visited"><div className="dot" /><span>Not Answered</span></div>
                <div className="legend-item visited"><div className="dot" /><span>Wrong Answer</span></div>
              </div>
            </div>

            <div className="actions">
              <div className="action-buttons">
                <button onClick={() => setQuestionPaperDialogOpen(true)}>Question Paper</button>
                <button onClick={() => setInstructionsDialogOpen(true)}>Instructions</button>
              </div>
              <button className="submit-btn" onClick={handleSubmitTest}>
                Submit Test
              </button>
            </div>

            <Dialog open={questionPaperDialogOpen} onOpenChange={setQuestionPaperDialogOpen}>
              <DialogContent className="dialog-content">
                <div className="dialog-inner">
                  <DialogHeader>
                    <DialogTitle>Question Paper - {activeSection}</DialogTitle>
                  </DialogHeader>
                  <div className="dialog-body">{/* Add Question Paper Component */}</div>
                </div>
              </DialogContent>
            </Dialog>

            <Dialog open={instructionsDialogOpen} onOpenChange={setInstructionsDialogOpen}>
              <DialogContent className="dialog-content">
                <div className="dialog-inner">
                  <DialogHeader>
                    <DialogTitle>General Instructions</DialogTitle>
                  </DialogHeader>
                  <div className="dialog-body">
                    Directions: In the following question, two columns are given...
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </div>
    </>
  );
};

export default QuestionDisplay;




